import {
    AudioTrackStats,
    CandidatePairStats,
    CandidateStats,
    Certificate,
    CodecStats,
    InboundRtpAudioStreamsStats,
    InboundRtpVideoStreamsStats,
    OutboundRtpAudioStreamStats,
    OutboundRtpVideoStreamStats,
    RemoteInboundRtpAudioStreamsStats,
    RemoteInboundRtpVideoStreamsStats,
    StatsReport,
    TransportStats,
    VideoRendererStats,
    VideoTrackStats,
} from '@yandex-360/telemost-sdk/models/stats';

import { bind } from './decorators';
import { EventEmitter } from './event-emitter';

const DEFAULT_COLLECT_STATS_INTERVAL = 10000;
export const TRACK_ID_ATTRIBUTE = 'data-track-id';
export const IS_SHARING_ATTRIBUTE = 'data-is-sharing';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StatsMap = Map<string, any>;

type PeerConnectionStatsOptions = {
    getPeerConnection: () => Maybe<RTCPeerConnection>;
    getParticipantIdByTrackId: (trackId: string) => string | undefined;
    onStats: (stats: StatsReport) => void;
    onRawStats?: (stats: RTCStatsReport) => void;
    interval?: number;
};

export class PeerConnectionStats extends EventEmitter {
    private readonly _getPeerConnection: () => Maybe<RTCPeerConnection>;
    private get _peerConnection(): Maybe<RTCPeerConnection> {
        return this._getPeerConnection();
    }
    private _collectStatsTimer: ReturnType<typeof setInterval> | undefined;
    private _collectStatsInterval: number;
    private _getParticipantIdByTrackId: PeerConnectionStatsOptions['getParticipantIdByTrackId'];
    private _onStats: PeerConnectionStatsOptions['onStats'];
    private _onRawStats?: PeerConnectionStatsOptions['onRawStats'];
    constructor({
        getPeerConnection,
        getParticipantIdByTrackId,
        onStats,
        onRawStats,
        interval = DEFAULT_COLLECT_STATS_INTERVAL,
    }: PeerConnectionStatsOptions) {
        super();
        this._getPeerConnection = getPeerConnection;
        this._getParticipantIdByTrackId = getParticipantIdByTrackId;
        this._onStats = onStats;
        this._onRawStats = onRawStats;
        this._collectStatsInterval = interval;

        this.startJob();
    }

    private _getCertificateById(id: string, statsMap: StatsMap): Certificate | undefined {
        const entry = statsMap.get(id);

        if (entry) {
            const result: Certificate = {
                fingerprint: entry.fingerprint,
            };

            return result;
        }

        return;
    }

    private getEntryTrackIdentifier(entryId: string, statsMap: StatsMap): string {
        const entry = statsMap.get(entryId);

        return (
            entry.trackIdentifier ||
            statsMap.get(entry.mediaSourceId)?.trackIdentifier ||
            statsMap.get(entry.trackId)?.trackIdentifier ||
            ''
        );
    }

    private _getVideoTrackStatsById(id: string, statsMap: StatsMap): VideoTrackStats | undefined {
        const entry = statsMap.get(id);

        if (entry) {
            const trackIdentifier = this.getEntryTrackIdentifier(id, statsMap);
            const videoRendererStats = this._getVideoRendererStats(id, trackIdentifier, statsMap);

            const result: VideoTrackStats = {
                track_identifier: trackIdentifier,
                peer_id: this._getPeerId(trackIdentifier),
                frame_width: entry.frameWidth,
                frame_height: entry.frameHeight,
                frames_sent: entry.framesSent,
                frames_dropped: entry.framesDropped,
                frames_received: entry.framesReceived,
                frames_per_second: entry.framesPerSecond,
                partial_frames_lost: entry.partialFramesLost,
                full_frames_lost: entry.fullFramesLost,
                active: entry.active,
                ...videoRendererStats,
            };

            return result;
        }

        return;
    }

    private _getVideoRenderer(trackIdentifier: string): Maybe<HTMLVideoElement> {
        const video = document.querySelector<HTMLVideoElement>(`video[${TRACK_ID_ATTRIBUTE}="${trackIdentifier}"]`);
        if (video) {
            return video;
        }
    }

    private _getRenderedVideoSize(videoNode?: HTMLVideoElement): Maybe<DOMRect> {
        return videoNode?.getBoundingClientRect();
    }

    private _getVideoContentType(id: string, statsMap: StatsMap, videoNode?: HTMLVideoElement) {
        const entry = statsMap.get(id);
        if (entry?.contentType) {
            return entry.contentType;
        }
        if (videoNode && Boolean(videoNode.getAttribute(IS_SHARING_ATTRIBUTE))) {
            return 'screenshare';
        }
    }

    private _getVideoRendererStats(id: string, trackIdentifier: string, statsMap: StatsMap): VideoRendererStats {
        const videoNode = this._getVideoRenderer(trackIdentifier);
        const rendererSize = this._getRenderedVideoSize(videoNode);
        const contentType = this._getVideoContentType(id, statsMap, videoNode);

        const stats: VideoRendererStats = {};
        if (rendererSize) {
            stats.rendered_video_width = rendererSize.width;
            stats.rendered_video_height = rendererSize.height;
        }
        if (contentType) {
            stats.content_type = contentType;
        }

        return stats;
    }

    private _getAudioTrackStatsById(id: string, statsMap: StatsMap): AudioTrackStats | undefined {
        const entry = statsMap.get(id);

        if (entry) {
            const trackIdentifier = this.getEntryTrackIdentifier(id, statsMap);
            const result: AudioTrackStats = {
                track_identifier: trackIdentifier,
                peer_id: this._getPeerId(trackIdentifier),
                concealed_samples: entry.concealedSamples,
                concealment_events: entry.concealmentEvents,
                total_samples_received: entry.totalSamplesReceived,
                total_samples_duration: entry.totalSamplesDuration,
                silent_concealed_samples: entry.silentConcealedSamples,
                total_audio_energy: entry.totalAudioEnergy,
                audio_level: entry.audioLevel,
                active: entry.active,
            };

            return result;
        }

        return;
    }

    private _getCodecStatsById(id: string, statsMap: StatsMap): CodecStats | undefined {
        const entry = statsMap.get(id);

        if (entry) {
            const result: CodecStats = {
                mime_type: entry.mimeType,
                payload_type: entry.payloadType,
                clock_rate: entry.clockRate,
            };

            return result;
        }

        return;
    }

    private _getCandidateStatsById(id: string, statsMap: StatsMap): CandidateStats | undefined {
        const entry = statsMap.get(id);

        if (entry) {
            const result: CandidateStats = {
                ip: entry.ip,
                port: entry.port,
                protocol: entry.protocol,
                relay_protocol: entry.relayProtocol,
                network_type: entry.networkType,
                candidate_type: entry.candidateType,
            };

            return result;
        }

        return;
    }

    private _getCandidatePairStatsById(id: string, statsMap: StatsMap): CandidatePairStats | undefined {
        const entry = statsMap.get(id);

        if (entry) {
            const result: CandidatePairStats = {
                id: entry.id,
                state: entry.state,
                available_incoming_bitrate: entry.availableIncomingBitrate,
                available_outgoing_bitrate: entry.availableOutgoingBitrate,
                packets_sent: entry.packetsSent,
                packets_received: entry.packetsReceived,
                bytes_received: entry.bytesReceived,
                bytes_sent: entry.bytesSent,
                current_round_trip_time: entry.currentRoundTripTime,
                total_round_trip_time: entry.totalRoundTripTime,
                nominated: entry.nominated,
                responses_received: entry.responsesReceived,
                local_candidate: this._getCandidateStatsById(entry.localCandidateId, statsMap),
                remote_candidate: this._getCandidateStatsById(entry.remoteCandidateId, statsMap),
                remote_candidate_id: entry.remoteCandidateId,
            };

            return result;
        }

        return;
    }

    private _getPeerId(trackId: string) {
        return this._getParticipantIdByTrackId(trackId) || '';
    }

    public startJob() {
        this._collectStatsTimer = setInterval(this._collectStats, this._collectStatsInterval);
    }

    public stopJob() {
        if (this._collectStatsTimer) {
            clearInterval(this._collectStatsTimer);
        }
    }

    @bind
    private async _collectStats() {
        if (!this._peerConnection) {
            return;
        }
        const stats = await this._peerConnection.getStats();

        const statsReport: StatsReport = {
            timestamp: 0,
            transport: {} as TransportStats,
            candidate_pair: [],
            inbound_rtp_audio_streams: [],
            inbound_rtp_video_streams: [],
            outbound_rtp_audio_stream: {} as OutboundRtpAudioStreamStats,
            outbound_rtp_video_stream: {} as OutboundRtpVideoStreamStats,
            other_outbound_rtp_audio_streams: [],
            other_outbound_rtp_video_streams: [],
            remote_inbound_rtp_audio_streams: [],
            remote_inbound_rtp_video_streams: [],
        };

        const statsMap: StatsMap = new Map();

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        for (const report of stats.values()) {
            statsMap.set(report.id, report);
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        for (const report of stats.values()) {
            switch (report.type) {
                case 'transport': {
                    statsReport.timestamp = Math.trunc(report.timestamp);
                    statsReport.transport = {
                        id: report.id,
                        selected_candidate_pair_id: report.selectedCandidatePairId,
                        bytes_received: report.bytesReceived,
                        bytes_sent: report.bytesSent,
                        dtls_state: report.dtlsState,
                        local_certificate: this._getCertificateById(report.localCertificateId, statsMap),
                        remote_certificate: this._getCertificateById(report.remoteCertificateId, statsMap),
                        selected_candidate_pair: this._getCandidatePairStatsById(
                            report.selectedCandidatePairId,
                            statsMap
                        ),
                    };

                    break;
                }

                case 'outbound-rtp': {
                    switch (report.kind) {
                        case 'video': {
                            const stats: OutboundRtpVideoStreamStats = {
                                ssrc: report.ssrc,
                                packets_sent: report.packetsSent,
                                total_packet_send_delay: report.totalPacketSendDelay,
                                bytes_sent: report.bytesSent,
                                codec: this._getCodecStatsById(report.codecId, statsMap),
                                nack_count: report.nackCount,
                                pli_count: report.pliCount,
                                fir_count: report.firCount,
                                frames_per_second: report.framesPerSecond,
                                frames_encoded: report.framesEncoded,
                                key_frames_encoded: report.keyFramesEncoded,
                                video_track: this._getVideoTrackStatsById(report.id, statsMap),
                                qp_sum: report.qpSum,
                                total_encode_time: report.totalEncodeTime,
                            };
                            statsReport.outbound_rtp_video_stream = stats;
                            statsReport.other_outbound_rtp_video_streams.push(stats);

                            break;
                        }

                        case 'audio': {
                            const stats: OutboundRtpAudioStreamStats = {
                                ssrc: report.ssrc,
                                packets_sent: report.packetsSent,
                                bytes_sent: report.bytesSent,
                                codec: this._getCodecStatsById(report.codecId, statsMap),
                                audio_track: this._getAudioTrackStatsById(report.id, statsMap),
                            };
                            statsReport.outbound_rtp_audio_stream = stats;
                            statsReport.other_outbound_rtp_audio_streams.push(stats);

                            break;
                        }
                    }
                    break;
                }

                case 'inbound-rtp': {
                    switch (report.kind) {
                        case 'video': {
                            const videoTrack = this._getVideoTrackStatsById(report.id, statsMap);
                            if (!videoTrack?.peer_id) {
                                break;
                            }

                            const streamStats: InboundRtpVideoStreamsStats = {
                                ssrc: report.ssrc,
                                jitter: report.jitter,
                                frames_decoded: report.framesDecoded,
                                key_frames_decoded: report.keyFramesDecoded,
                                frames_per_second: report.framesPerSecond,
                                nack_count: report.nackCount,
                                packets_repaired: report.packetsRepaired,
                                packets_duplicated: report.packetsDuplicated,
                                fec_packets_discarded: report.fecPacketsDiscarded,
                                fec_packets_received: report.fecPacketsReceived,
                                packets_lost: report.packetsLost,
                                packets_received: report.packetsReceived,
                                packets_discarded: report.packetsDiscarded,
                                bytes_received: report.bytesReceived,
                                pli_count: report.pliCount,
                                fraction_lost: report.fractionLost,
                                total_decode_time: report.totalDecodeTime,
                                total_freezes_duration: report.totalFreezesDuration,
                                qp_sum: report.qpSum,
                                video_track: videoTrack,
                                codec: this._getCodecStatsById(report.codecId, statsMap),
                            };
                            statsReport.inbound_rtp_video_streams.push(streamStats);

                            break;
                        }
                        case 'audio': {
                            const audioTrack = this._getAudioTrackStatsById(report.id, statsMap);
                            if (!audioTrack?.peer_id) {
                                break;
                            }

                            const streamStats: InboundRtpAudioStreamsStats = {
                                ssrc: report.ssrc,
                                fec_packets_discarded: report.fecPacketsDiscarded,
                                fec_packets_received: report.fecPacketsReceived,
                                packets_lost: report.packetsLost,
                                packets_received: report.packetsReceived,
                                packets_discarded: report.packetsDiscarded,
                                packets_repaired: report.packetsRepaired,
                                packets_duplicated: report.packetsDuplicated,
                                bytes_received: report.bytesReceived,
                                jitter: report.jitter,
                                fraction_lost: report.fractionLost,
                                audio_track: audioTrack,
                                codec: this._getCodecStatsById(report.codecId, statsMap),
                            };

                            statsReport.inbound_rtp_audio_streams.push(streamStats);

                            break;
                        }
                    }

                    break;
                }
                case 'remote-inbound-rtp': {
                    switch (report.kind) {
                        case 'video': {
                            const videoTrack = this._getVideoTrackStatsById(report.localId, statsMap);

                            if (!videoTrack?.peer_id) {
                                break;
                            }

                            const streamStats: RemoteInboundRtpVideoStreamsStats = {
                                ssrc: report.ssrc,
                                jitter: report.jitter,
                                packets_lost: report.packetsLost,
                                round_trip_time: report.roundTripTime,
                                video_track: videoTrack,
                                codec: this._getCodecStatsById(report.codecId, statsMap),
                            };

                            statsReport.remote_inbound_rtp_video_streams.push(streamStats);

                            break;
                        }
                        case 'audio': {
                            const audioTrack = this._getAudioTrackStatsById(report.localId, statsMap);
                            if (!audioTrack?.peer_id) {
                                break;
                            }

                            const streamStats: RemoteInboundRtpAudioStreamsStats = {
                                ssrc: report.ssrc,
                                jitter: report.jitter,
                                packets_lost: report.packetsLost,
                                round_trip_time: report.roundTripTime,
                                audio_track: audioTrack,
                                codec: this._getCodecStatsById(report.codecId, statsMap),
                            };

                            statsReport.remote_inbound_rtp_audio_streams.push(streamStats);
                            break;
                        }
                    }
                    break;
                }
                case 'candidate-pair': {
                    const candidatePair = this._getCandidatePairStatsById(report.id, statsMap);
                    if (candidatePair) {
                        statsReport.candidate_pair.push(candidatePair);
                    }
                    break;
                }
            }
        }

        this.emit('rtc-stats', statsReport);
        this._onStats(statsReport);
        this._onRawStats?.(stats);
    }
}
